import { useCallback, useEffect, useState } from 'react'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { LOCATION_EMAIL_IDENTITIES_QUERY } from 'app/components/location-email-identity/graphql'
import { gqlFetchOne, gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { SortDirection } from 'app/generated/graphql'
import { logError } from 'lib/logging'
import {
  CREATE_SENDING_DOMAIN_MUTATION,
  DELETE_SENDING_DOMAIN_MUTATION,
  SENDING_DOMAINS_QUERY,
  VERIFY_SENDING_DOMAIN_MUTATION,
} from './graphql'

export type SendingDomainRow = ResultOf<typeof SENDING_DOMAINS_QUERY>['sendingDomains']['hits'][number]

export type UseSendingDomainsReturn = {
  domains: SendingDomainRow[]
  loading: boolean
  newDomain: string
  setNewDomain: (v: string) => void
  adding: boolean
  verifyingId: string | undefined
  onAddDomain: () => Promise<void>
  onVerify: (id: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

export function useSendingDomains(): UseSendingDomainsReturn {
  const [domains, setDomains] = useState<SendingDomainRow[]>([])
  const [loading, setLoading] = useState(true)
  const [newDomain, setNewDomain] = useState(``)
  const [adding, setAdding] = useState(false)
  const [verifyingId, setVerifyingId] = useState<string | undefined>(undefined)
  const [confirm] = useConfirmation()

  // `network-only` on reload so a refetch after create/verify/delete bypasses
  // the urql document cache, which would otherwise return the stale list.
  const load = useCallback(async () => {
    setLoading(true)
    const result = await gqlQuery(
      SENDING_DOMAINS_QUERY,
      { sort: { field: `createdDate`, direction: SortDirection.Desc } },
      { requestPolicy: `network-only` },
    )
    if (result.error) {
      notifyError(`Failed to load sending domains`)
      logError(`Failed to load sending domains`, result.error)
      setLoading(false)
      return
    }
    setDomains(result.data?.sendingDomains.hits ?? [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load().catch((err) => logError(`Failed to load sending domains`, err))
  }, [load])

  async function onAddDomain(): Promise<void> {
    const domain = newDomain.trim().toLowerCase()
    if (!domain) return

    setAdding(true)
    const result = await gqlMutation(CREATE_SENDING_DOMAIN_MUTATION, { input: { domain } })
    setAdding(false)

    if (result.error) {
      notifyError(result.error.message || `Failed to add domain`)
      return
    }
    // The API answers a domain the workspace already has with that domain instead of an error.
    if (domains.some((row) => row.id === result.data?.createSendingDomain.id)) {
      notifyError(`This domain is already in use.`)
      return
    }
    notifySuccess(`Domain added. Publish the DNS records, then verify.`)
    setNewDomain(``)
    await load()
  }

  async function onVerify(id: string): Promise<void> {
    setVerifyingId(id)
    const result = await gqlMutation(VERIFY_SENDING_DOMAIN_MUTATION, { id })
    setVerifyingId(undefined)

    if (result.error) {
      notifyError(result.error.message || `Failed to check verification`)
      return
    }
    const status = result.data?.verifySendingDomain.status
    if (status === `verified`) notifySuccess(`Domain verified`)
    else notifySuccess(`Still ${status ?? `pending`}. DNS can take time to propagate.`)
    await load()
  }

  // Deleting a domain also deletes the email identity of every location on it, so the confirm names them.
  async function onDelete(id: string): Promise<void> {
    const domain = domains.find((row) => row.id === id)?.domain ?? `this domain`
    const identitiesResult = await gqlQuery(
      LOCATION_EMAIL_IDENTITIES_QUERY,
      { search: `sendingDomainId.keyword:${id}` },
      { requestPolicy: `network-only` },
    )
    if (identitiesResult.error) {
      notifyError(`Couldn't check which locations use ${domain}`)
      logError(`Failed to load identities for sending domain`, identitiesResult.error)
      return
    }
    const locationNames = await Promise.all(
      (identitiesResult.data?.locationEmailIdentities.hits ?? []).map(async (identity) => {
        const location = await gqlFetchOne<{ name?: string }>(`location`, identity.locationId)
        return location?.name ?? identity.address
      }),
    )

    try {
      await confirm({
        variant: `danger`,
        title: `Remove ${domain}?`,
        description: locationNames.length
          ? `This also removes the email settings of ${locationNames.join(`, `)}: From address, From name and Reply-To. ${locationNames.length === 1 ? `It` : `They`} can't send email until ${locationNames.length === 1 ? `it's` : `they're`} set up again.`
          : `No location sends from this domain.`,
        submitLabel: `Remove`,
        cancelLabel: `Cancel`,
      })
      // oxlint-disable-next-line local/require-catch-logging
    } catch {
      return
    }

    const result = await gqlMutation(DELETE_SENDING_DOMAIN_MUTATION, { id })
    if (result.error) {
      notifyError(result.error.message || `Failed to remove domain`)
      return
    }
    notifySuccess(`Domain removed`)
    await load()
  }

  return {
    domains,
    loading,
    newDomain,
    setNewDomain,
    adding,
    verifyingId,
    onAddDomain,
    onVerify,
    onDelete,
  }
}
